export const GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION = '1.0.0';

export interface PersistedGraphNodeTypeVisualConfig {
	labelsVisible: boolean;
	colorHex: string;
	brightness: number;
}

export interface GraphAppearanceConfig {
	schemaVersion: string;
	nodeTypeVisuals: Record<string, PersistedGraphNodeTypeVisualConfig>;
}

const DEFAULT_GRAPH_NODE_TYPE_VISUAL_PRESETS: Record<string, PersistedGraphNodeTypeVisualConfig> = {
	Human: {
		labelsVisible: true,
		colorHex: '#ffd37a',
		brightness: 1.08,
	},
	Project: {
		labelsVisible: false,
		colorHex: '#67d7ff',
		brightness: 0.96,
	},
	Occupation: {
		labelsVisible: false,
		colorHex: '#c89bff',
		brightness: 0.9,
	},
};

export function createDefaultGraphAppearanceConfig(): GraphAppearanceConfig {
	return {
		schemaVersion: GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION,
		nodeTypeVisuals: {},
	};
}

export function presetGraphNodeTypeVisualConfig(typeId: string): PersistedGraphNodeTypeVisualConfig {
	return DEFAULT_GRAPH_NODE_TYPE_VISUAL_PRESETS[typeId] ?? {
		labelsVisible: false,
		colorHex: '#8ec7ff',
		brightness: 0.92,
	};
}

export function normalizePersistedGraphNodeTypeVisualConfig(
	typeId: string,
	config?: Partial<PersistedGraphNodeTypeVisualConfig> | null,
): PersistedGraphNodeTypeVisualConfig {
	const preset = presetGraphNodeTypeVisualConfig(typeId);

	return {
		labelsVisible: typeof config?.labelsVisible === 'boolean' ? config.labelsVisible : preset.labelsVisible,
		colorHex: normalizeGraphAppearanceHexColor(config?.colorHex, preset.colorHex),
		brightness: typeof config?.brightness === 'number'
			? clampGraphAppearanceBrightness(config.brightness)
			: preset.brightness,
	};
}

export function normalizeGraphAppearanceHexColor(candidate: unknown, fallbackHexColor: string): string {
	if (typeof candidate !== 'string') {
		return fallbackHexColor;
	}

	const normalized = candidate.trim().toLowerCase();
	if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
		return fallbackHexColor;
	}

	if (normalized.length === 4) {
		return `#${normalized
			.slice(1)
			.split('')
			.map((value) => `${value}${value}`)
			.join('')}`;
	}

	return normalized;
}

export function clampGraphAppearanceBrightness(brightness: number): number {
	return Math.min(1.3, Math.max(0.45, brightness));
}